import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  Box,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Stack,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DataSourceService from '@/core/services/dataSource';

interface ValidationStep {
  id: number;
  label: string;
  ok: boolean;
  error: string | null;
}

interface CookieInfo {
  name: string;
  value: string;
  decodedPayload?: Record<string, unknown> | null;
  isLikelyJWT: boolean;
  length: number;
  notes?: string | null;
}

interface CookieSecurityReport {
  ci_session: CookieInfo | null;
  ci_user: CookieInfo | null;
  steps: ValidationStep[];
  summary: { passed: number; failed: number };
}

interface CookieReportResponse {
  received: boolean;
  raw: string;
  report: CookieSecurityReport;
  metadata?: Record<string, unknown>;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const dataSource = DataSourceService({ baseUrl: '' });

const CookieReportModal: React.FC<Props> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<CookieReportResponse | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (open) {
      setLoading(true);
      setError(null);
      dataSource
        .getCookieReport()
        .then((r) => setReport(r))
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 6 }}>
        Reporte de Cookies Recibidas
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography color="error" variant="body2">
            Error: {error}
          </Typography>
        )}
        {!loading && report && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Validación de Cookies Clave
            </Typography>
            <Stepper
              activeStep={report.report.steps.filter((s) => s.ok).length}
              alternativeLabel
              sx={{ mb: 2 }}
            >
              {report.report.steps.map((step) => (
                <Step key={step.id} completed={step.ok}>
                  <StepLabel error={!step.ok}>{step.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Stack spacing={1} sx={{ mb: 2 }}>
              {report.report.steps.map((step) => (
                <Box key={step.id} display="flex" alignItems="center" gap={1}>
                  <Chip
                    size="small"
                    color={step.ok ? 'success' : 'error'}
                    label={step.ok ? 'OK' : 'FALLA'}
                  />
                  <Typography variant="body2">
                    {step.label}{' '}
                    {step.error && !step.ok && (
                      <Typography component="span" color="error" variant="caption">
                        {' '}
                        - {step.error}
                      </Typography>
                    )}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Detalle de Cookies
            </Typography>
            {report.metadata && (
              <Typography variant="caption" display="block" gutterBottom>
                Metadata: {JSON.stringify(report.metadata)}
              </Typography>
            )}
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Nombre</TableCell>
                  <TableCell>Largo</TableCell>
                  <TableCell>JWT</TableCell>
                  <TableCell>Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[report.report.ci_session, report.report.ci_user].filter(Boolean).map((c) => {
                  const cookie = c as CookieInfo;
                  const isOpen = expanded[cookie.name];
                  return (
                    <React.Fragment key={cookie.name}>
                      <TableRow hover>
                        <TableCell padding="checkbox">
                          {cookie.decodedPayload ? (
                            <IconButton
                              size="small"
                              onClick={() =>
                                setExpanded((prev) => ({
                                  ...prev,
                                  [cookie.name]: !prev[cookie.name],
                                }))
                              }
                            >
                              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          ) : null}
                        </TableCell>
                        <TableCell>{cookie.name}</TableCell>
                        <TableCell>{cookie.length}</TableCell>
                        <TableCell>
                          {cookie.isLikelyJWT ? (
                            <Chip color="primary" size="small" label="JWT" />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={cookie.value} arrow>
                            <Typography
                              variant="caption"
                              sx={{
                                maxWidth: 200,
                                display: 'inline-block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {cookie.value}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {cookie.decodedPayload && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                            <Collapse in={Boolean(isOpen)} timeout="auto" unmountOnExit>
                              <Box p={2} bgcolor="grey.50">
                                <Typography variant="subtitle2" gutterBottom>
                                  Payload Decodificado
                                </Typography>
                                <pre
                                  style={{
                                    margin: 0,
                                    maxHeight: 240,
                                    overflow: 'auto',
                                    background: '#f5f5f5',
                                    padding: '8px',
                                    borderRadius: 4,
                                  }}
                                >
                                  {JSON.stringify(cookie.decodedPayload, null, 2)}
                                </pre>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button
          onClick={() => {
            if (report) {
              const blob = new Blob([JSON.stringify(report, null, 2)], {
                type: 'application/json',
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'cookie-report.json';
              a.click();
              URL.revokeObjectURL(url);
            }
          }}
        >
          Exportar JSON
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CookieReportModal;
